import React, { useState, useEffect, useRef } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import {
  ArrowLeft,
  Send,
  Loader2,
  AlertCircle,
  CheckCircle,
  XCircle,
  Clock,
  MessageSquare,
  User,
  Headphones,
  Star,
  RefreshCw,
  Ticket
} from 'lucide-react';
import { apiRequest } from '../../api/client';
import { toast } from '../../utils/toastUtils';
import { useI18n } from '../../i18n';

const getStatusConfig = (t) => ({
  open: { label: t('support.statuses.open'), color: 'text-blue-400', bg: 'bg-blue-500/10', icon: AlertCircle },
  in_progress: { label: t('support.statuses.in_progress'), color: 'text-yellow-400', bg: 'bg-yellow-500/10', icon: Loader2 },
  waiting_customer: { label: t('support.statuses.waiting_customer'), color: 'text-orange-400', bg: 'bg-orange-500/10', icon: Clock },
  waiting_internal: { label: t('support.statuses.waiting_internal'), color: 'text-purple-400', bg: 'bg-purple-500/10', icon: Clock },
  resolved: { label: t('support.statuses.resolved'), color: 'text-green-400', bg: 'bg-green-500/10', icon: CheckCircle },
  closed: { label: t('support.statuses.closed'), color: 'text-gray-400', bg: 'bg-gray-500/10', icon: XCircle }
});

const getPriorityConfig = (t) => ({
  low: { label: t('support.priorities.low'), color: 'text-gray-400', bg: 'bg-gray-500/10' },
  medium: { label: t('support.priorities.medium'), color: 'text-blue-400', bg: 'bg-blue-500/10' },
  high: { label: t('support.priorities.high'), color: 'text-orange-400', bg: 'bg-orange-500/10' },
  urgent: { label: t('support.priorities.urgent'), color: 'text-red-400', bg: 'bg-red-500/10' }
});

const getCategoryConfig = (t) => ({
  technical: { label: t('support.categories.technical'), color: 'text-cyan-400' },
  billing: { label: t('support.categories.billing'), color: 'text-green-400' },
  feature_request: { label: t('support.categories.feature_request'), color: 'text-purple-400' },
  bug_report: { label: t('support.categories.bug_report'), color: 'text-red-400' },
  account: { label: t('support.categories.account'), color: 'text-blue-400' },
  security: { label: t('support.categories.security'), color: 'text-yellow-400' },
  other: { label: t('support.categories.other'), color: 'text-gray-400' }
});

const TicketDetails = () => {
  const { t } = useI18n();
  const { id } = useParams();
  const navigate = useNavigate();
  const messagesEndRef = useRef(null);

  const [ticket, setTicket] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newMessage, setNewMessage] = useState('');
  const [sending, setSending] = useState(false);
  const [closing, setClosing] = useState(false);
  const [showRating, setShowRating] = useState(false);
  const [rating, setRating] = useState(0);
  const [feedback, setFeedback] = useState('');

  const statusConfig = getStatusConfig(t);
  const priorityConfig = getPriorityConfig(t);
  const categoryConfig = getCategoryConfig(t);

  const fetchTicket = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await apiRequest(`/api/v1/support/tickets/${id}`);
      setTicket(data);
    } catch (err) {
      console.error('Error fetching ticket:', err);
      setError(t('support.errors.loadingTicket'));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchTicket();
  }, [id]);

  useEffect(() => {
    // Scroll to bottom when new messages arrive
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [ticket?.messages]);

  const handleSendMessage = async (e) => {
    e.preventDefault();
    if (!newMessage.trim()) return;

    try {
      setSending(true);
      await apiRequest(`/api/v1/support/tickets/${id}/messages`, {
        method: 'POST',
        body: JSON.stringify({ message: newMessage.trim() })
      });
      setNewMessage('');
      toast.success(t('support.success.messageSent'));
      await fetchTicket();
    } catch (err) {
      console.error('Error sending message:', err);
      toast.error(err.message || t('support.errors.sendingMessage'));
    } finally {
      setSending(false);
    }
  };

  const handleCloseTicket = async () => {
    try {
      setClosing(true);
      let url = `/api/v1/support/tickets/${id}/close`;
      if (rating > 0) {
        url += `?satisfaction_rating=${rating}`;
        if (feedback) {
          url += `&satisfaction_feedback=${encodeURIComponent(feedback)}`;
        }
      }
      await apiRequest(url, { method: 'POST' });
      toast.success(t('support.success.ticketClosed'));
      await fetchTicket();
      setShowRating(false);
    } catch (err) {
      console.error('Error closing ticket:', err);
      toast.error(err.message || t('support.errors.closingTicket'));
    } finally {
      setClosing(false);
    }
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('fr-FR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  if (loading) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-center py-20">
          <Loader2 className="w-8 h-8 text-brand-green animate-spin" />
        </div>
      </div>
    );
  }

  if (error || !ticket) {
    return (
      <div className="container mx-auto px-6 py-8">
        <div className="liquid-glass rounded-xl p-8 text-center">
          <AlertCircle className="w-12 h-12 text-red-400 mx-auto mb-4" />
          <p className="text-red-400">{error || t('support.errors.ticketNotFound')}</p>
          <Link
            to="/support/tickets"
            className="mt-4 inline-flex items-center gap-2 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all"
          >
            <ArrowLeft className="w-4 h-4" />
            {t('support.backToTickets')}
          </Link>
        </div>
      </div>
    );
  }

  const status = statusConfig[ticket.status] || statusConfig.open;
  const priority = priorityConfig[ticket.priority] || priorityConfig.medium;
  const category = categoryConfig[ticket.category] || categoryConfig.other;
  const StatusIcon = status.icon;
  const isClosed = ticket.status === 'closed';
  const canClose = !isClosed && ticket.status !== 'open';

  return (
    <div className="container mx-auto px-6 py-8">
      {/* Header */}
      <div className="mb-6">
        <Link
          to="/support/tickets"
          className="inline-flex items-center gap-2 text-gray-400 hover:text-brand-green transition-colors mb-4"
        >
          <ArrowLeft className="w-4 h-4" />
          {t('support.backToTickets')}
        </Link>

        <div className="flex flex-col md:flex-row md:items-start md:justify-between gap-4">
          <div>
            <div className="flex items-center gap-3 mb-2">
              <span className="text-brand-green font-mono text-lg">{ticket.ticket_number}</span>
              <span className={`px-3 py-1 rounded-full text-sm font-medium ${status.bg} ${status.color}`}>
                <StatusIcon className="w-4 h-4 inline mr-1" />
                {status.label}
              </span>
            </div>
            <h1 className="text-2xl font-bold text-white">{ticket.subject}</h1>
            <div className="flex items-center gap-4 mt-2 text-sm">
              <span className={`${priority.bg} ${priority.color} px-2 py-0.5 rounded`}>
                {t('support.priorities.priority')} {priority.label}
              </span>
              <span className={category.color}>{category.label}</span>
              <span className="text-gray-500">
                {t('support.createdOn')} {formatDate(ticket.created_at)}
              </span>
            </div>
          </div>

          <div className="flex gap-2">
            <button
              onClick={fetchTicket}
              className="flex items-center gap-2 px-4 py-2 bg-dark-800/50 border border-brand-green/20 rounded-lg text-gray-400 hover:text-brand-green hover:border-brand-green/50 transition-all"
            >
              <RefreshCw className="w-4 h-4" />
              {t('support.refresh')}
            </button>
            {canClose && (
              <button
                onClick={() => setShowRating(true)}
                className="flex items-center gap-2 px-4 py-2 bg-green-500/10 border border-green-500/20 rounded-lg text-green-400 hover:bg-green-500/20 transition-all"
              >
                <CheckCircle className="w-4 h-4" />
                {t('support.closeTicket')}
              </button>
            )}
          </div>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        {/* Messages */}
        <div className="lg:col-span-2 space-y-4">
          {/* Original Description */}
          <div className="liquid-glass rounded-xl p-6">
            <div className="flex items-start gap-4">
              <div className="w-10 h-10 rounded-full bg-brand-green/20 flex items-center justify-center flex-shrink-0">
                <User className="w-5 h-5 text-brand-green" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2 mb-2">
                  <span className="font-medium text-white">{t('support.you')}</span>
                  <span className="text-gray-500 text-sm">{formatDate(ticket.created_at)}</span>
                </div>
                <div className="text-gray-300 whitespace-pre-wrap">{ticket.description}</div>
              </div>
            </div>
          </div>

          {/* Messages List */}
          {ticket.messages && ticket.messages.length > 0 && (
            <div className="space-y-4">
              {ticket.messages.map((msg) => (
                <div
                  key={msg.id}
                  className={`liquid-glass rounded-xl p-6 ${
                    msg.is_staff ? 'border-l-4 border-brand-green' : ''
                  } ${msg.is_system ? 'bg-gray-800/30' : ''}`}
                >
                  <div className="flex items-start gap-4">
                    <div className={`w-10 h-10 rounded-full flex items-center justify-center flex-shrink-0 ${
                      msg.is_staff
                        ? 'bg-brand-green/20'
                        : msg.is_system
                        ? 'bg-gray-600/20'
                        : 'bg-blue-500/20'
                    }`}>
                      {msg.is_staff ? (
                        <Headphones className="w-5 h-5 text-brand-green" />
                      ) : msg.is_system ? (
                        <AlertCircle className="w-5 h-5 text-gray-400" />
                      ) : (
                        <User className="w-5 h-5 text-blue-400" />
                      )}
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-2">
                        <span className={`font-medium ${
                          msg.is_staff ? 'text-brand-green' : msg.is_system ? 'text-gray-400' : 'text-white'
                        }`}>
                          {msg.is_staff ? t('support.supportTeam') : msg.is_system ? t('support.system') : t('support.you')}
                        </span>
                        <span className="text-gray-500 text-sm">{formatDate(msg.created_at)}</span>
                      </div>
                      <div className="text-gray-300 whitespace-pre-wrap">{msg.message}</div>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          )}

          <div ref={messagesEndRef} />

          {/* Reply Form */}
          {!isClosed && (
            <form onSubmit={handleSendMessage} className="liquid-glass rounded-xl p-6">
              <div className="flex items-start gap-4">
                <div className="w-10 h-10 rounded-full bg-blue-500/20 flex items-center justify-center flex-shrink-0">
                  <User className="w-5 h-5 text-blue-400" />
                </div>
                <div className="flex-1">
                  <textarea
                    value={newMessage}
                    onChange={(e) => setNewMessage(e.target.value)}
                    placeholder={t('support.writeReply')}
                    rows={4}
                    className="w-full px-4 py-3 bg-dark-800/50 border border-brand-green/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none"
                    disabled={sending}
                  />
                  <div className="flex justify-end mt-3">
                    <button
                      type="submit"
                      disabled={!newMessage.trim() || sending}
                      className="flex items-center gap-2 px-6 py-2.5 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {sending ? (
                        <>
                          <Loader2 className="w-4 h-4 animate-spin" />
                          {t('support.sending')}
                        </>
                      ) : (
                        <>
                          <Send className="w-4 h-4" />
                          {t('support.send')}
                        </>
                      )}
                    </button>
                  </div>
                </div>
              </div>
            </form>
          )}

          {/* Closed Notice */}
          {isClosed && (
            <div className="bg-gray-800/30 border border-gray-700 rounded-xl p-6 text-center">
              <XCircle className="w-8 h-8 text-gray-400 mx-auto mb-2" />
              <p className="text-gray-400">{t('support.ticketClosed')}</p>
              <Link
                to="/support/tickets/new"
                className="inline-flex items-center gap-2 mt-4 px-4 py-2 bg-brand-green/20 text-brand-green rounded-lg hover:bg-brand-green/30 transition-all"
              >
                <Ticket className="w-4 h-4" />
                {t('support.newTicket')}
              </Link>
            </div>
          )}
        </div>

        {/* Sidebar */}
        <div className="space-y-4">
          {/* Ticket Info */}
          <div className="liquid-glass rounded-xl p-6">
            <h3 className="text-lg font-semibold text-white mb-4">{t('support.informations')}</h3>
            <div className="space-y-4">
              <div>
                <span className="text-gray-500 text-sm">{t('support.ticketNumber')}</span>
                <p className="text-brand-green font-mono">{ticket.ticket_number}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('support.status')}</span>
                <p className={status.color}>{status.label}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('support.priority')}</span>
                <p className={priority.color}>{priority.label}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('support.category')}</span>
                <p className={category.color}>{category.label}</p>
              </div>
              <div>
                <span className="text-gray-500 text-sm">{t('support.createdOn')}</span>
                <p className="text-white">{formatDate(ticket.created_at)}</p>
              </div>
              {ticket.resolved_at && (
                <div>
                  <span className="text-gray-500 text-sm">{t('support.resolvedOn')}</span>
                  <p className="text-green-400">{formatDate(ticket.resolved_at)}</p>
                </div>
              )}
              {ticket.resolution_notes && (
                <div>
                  <span className="text-gray-500 text-sm">{t('support.resolutionNotes')}</span>
                  <p className="text-gray-300 text-sm mt-1">{ticket.resolution_notes}</p>
                </div>
              )}
              {ticket.satisfaction_rating && (
                <div>
                  <span className="text-gray-500 text-sm">{t('support.yourRating')}</span>
                  <div className="flex items-center gap-1 mt-1">
                    {[1, 2, 3, 4, 5].map((star) => (
                      <Star
                        key={star}
                        className={`w-5 h-5 ${
                          star <= ticket.satisfaction_rating
                            ? 'text-yellow-400 fill-yellow-400'
                            : 'text-gray-600'
                        }`}
                      />
                    ))}
                  </div>
                </div>
              )}
            </div>
          </div>

          {/* Help Box */}
          <div className="bg-brand-green/5 border border-brand-green/20 rounded-xl p-4">
            <h4 className="text-white font-medium mb-2">{t('support.needHelp')}</h4>
            <p className="text-gray-400 text-sm">
              {t('support.supportResponseDesc')}
            </p>
          </div>
        </div>
      </div>

      {/* Rating Modal */}
      {showRating && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4">
          <div className="liquid-glass rounded-2xl p-6 w-full max-w-md">
            <h3 className="text-xl font-bold text-white mb-4">{t('support.closeTicket')}</h3>
            <p className="text-gray-400 mb-6">
              {t('support.rateSupport')}
            </p>

            {/* Star Rating */}
            <div className="flex justify-center gap-2 mb-6">
              {[1, 2, 3, 4, 5].map((star) => (
                <button
                  key={star}
                  onClick={() => setRating(star)}
                  className="p-1 transition-transform hover:scale-110"
                >
                  <Star
                    className={`w-8 h-8 ${
                      star <= rating
                        ? 'text-yellow-400 fill-yellow-400'
                        : 'text-gray-600 hover:text-gray-400'
                    }`}
                  />
                </button>
              ))}
            </div>

            {/* Feedback */}
            <textarea
              value={feedback}
              onChange={(e) => setFeedback(e.target.value)}
              placeholder={t('support.comment')}
              rows={3}
              className="w-full px-4 py-3 bg-dark-800/50 border border-brand-green/20 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-brand-green/50 resize-none mb-6"
            />

            {/* Actions */}
            <div className="flex gap-3">
              <button
                onClick={() => setShowRating(false)}
                className="flex-1 px-4 py-2.5 text-gray-400 hover:text-white transition-colors"
              >
                {t('support.cancel')}
              </button>
              <button
                onClick={handleCloseTicket}
                disabled={closing}
                className="flex-1 flex items-center justify-center gap-2 px-4 py-2.5 bg-brand-green text-black font-semibold rounded-lg hover:bg-brand-green/90 transition-all disabled:opacity-50"
              >
                {closing ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    {t('support.closing')}
                  </>
                ) : (
                  <>
                    <CheckCircle className="w-4 h-4" />
                    {t('support.confirm')}
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TicketDetails;
